import React from 'react';
import { Link } from 'react-router-dom';
import HighlightedText from './HighlightedText';

import { getEntityColumns, formatCellValue, getEntityEmptyMessage } from '../../utils/entityHelpers';

/**
 * Renderiza un botón de acción para tabla o cards.
 *
 * @param {Object} action - Configuración de la acción
 * @param {Object} row - Fila de datos
 * @param {string} buttonClassName - Clase CSS del botón (ej: 'btn-table-action')
 * @returns {JSX.Element}
 */
export function renderActionButton(action, row, buttonClassName) {
  const isDisabled = action.disabled ? action.disabled(row) : false;
  if (action.to) {
    const href = typeof action.to === 'function' ? action.to(row) : action.to;
    return (
      <Link
        key={action.title}
        to={href}
        className={`btn ${buttonClassName} ${action.className}${isDisabled ? ' disabled' : ''}`}
        title={action.title}
        onClick={(e) => e.stopPropagation()}
      >
        <i className={`fas ${action.icon}`}></i>
        {action.label && buttonClassName !== 'btn-table-action' && ` ${action.label}`}
      </Link>
    );
  }
  return (
    <button
      key={action.title}
      className={`btn ${buttonClassName} ${action.className}`}
      onClick={(e) => { e.stopPropagation(); action.onClick(row); }}
      title={action.title}
      disabled={isDisabled}
    >
      <i className={`fas ${action.icon}`}></i>
      {action.label && buttonClassName !== 'btn-table-action' && ` ${action.label}`}
    </button>
  );
}

/**
 * Fuente única para el renderizado de contenido de celda.
 * Usada por el tbody y las cards mobile.
 *
 * @param {Object} column - Configuración de la columna
 * @param {Object} row - Fila de datos
 * @param {string} searchTerm - Término de búsqueda para highlighting
 * @returns {JSX.Element|string|number}
 */
function renderCellValue(column, row, searchTerm) {
  if (column.render) return column.render(row, searchTerm);

  const value = row[column.key];
  const formattedValue = formatCellValue(column.key, value, row);

  if (column.highlightable && searchTerm && (typeof value === 'string' || typeof value === 'number')) {
    return <HighlightedText text={String(value)} searchTerm={searchTerm} />;
  }

  // Badge-HTML: encapsulado aquí — el llamador no necesita detectarlo
  if (typeof formattedValue === 'string' && formattedValue.includes('<span class="badge')) {
    return <span dangerouslySetInnerHTML={{ __html: formattedValue }} />;
  }

  return formattedValue;
}

/**
 * Tabla genérica reutilizable para mostrar entidades.
 * Soporta ordenamiento por columnas, highlighting de búsqueda,
 * y botones de acción (editar, eliminar, historial).
 * En mobile (≤768px), renderiza automáticamente una vista de cards.
 *
 * @param {Object} props - Propiedades del componente
 * @param {Array} [props.data=[]] - Array de objetos a mostrar en la tabla
 * @param {Array} [props.columns] - Configuración de columnas (sobrescribe type si se proporciona)
 * @param {string} [props.type] - Tipo de entidad para columnas predefinidas (usado si no hay columns)
 * @param {Array<Object>} [props.actions=[]] - Array de acciones para la columna 'acciones'.
 *   Cada objeto: icon {string}, className {string}, title {string},
 *   onClick {Function} (handler que recibe row, requerido si no hay `to`),
 *   to {string|Function} (URL para Link de react-router, opcional),
 *   label {string} (texto visible en botón mobile, opcional),
 *   show {Function} (recibe row, retorna boolean, opcional),
 *   disabled {Function} (recibe row, retorna boolean, opcional).
 * @param {Function} [props.onSort] - Callback al ordenar, recibe (columnKey)
 * @param {string} [props.sortField=''] - Campo actualmente ordenado
 * @param {string} [props.sortOrder='asc'] - Dirección del orden: 'asc' o 'desc'
 * @param {boolean} [props.loading=false] - Si true, muestra estado de carga
 * @param {string} [props.searchTerm=''] - Término para resaltar coincidencias en celdas
 * @param {string} [props.emptyMessage] - Mensaje cuando no hay datos (tiene default por type)
 * @param {Function} [props.getRowClassName] - Función opcional que recibe row y retorna clase CSS
 * @param {boolean} [props.animateRows=false] - Si true, anima entrada de filas al cambiar datos
 * @param {Function} [props.mobileCardRenderer] - Renderer opcional para cards mobile.
 *   Si se proporciona, reemplaza el render genérico. Recibe (row, index) y retorna JSX.
 * @returns {JSX.Element} Tabla, estado de carga, o estado vacío
 */
function EntityTable({
  data = [],
  columns: customColumns,
  type,
  onSort,
  sortField = '',
  sortOrder = 'asc',
  loading = false,
  searchTerm = '',
  emptyMessage,
  getRowClassName,
  animateRows = false,
  actions = [],
  mobileCardRenderer,
}) {
  const columns = customColumns || getEntityColumns(type);

  /**
   * Maneja el ordenamiento por columna
   * @param {string} columnKey - Clave de la columna a ordenar
   */
  const handleSort = (columnKey) => {
    if (onSort && columns.find(col => col.key === columnKey)?.sortable) {
      onSort(columnKey);
    }
  };

  // Estado de carga
  if (loading) {
    return (
      <div className="loading-state">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Cargando datos...</p>
      </div>
    );
  }

  // Estado vacío
  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <i className="fas fa-filter"></i>
        <h3>No hay resultados</h3>
        <p>{emptyMessage || getEntityEmptyMessage(type)}</p>
      </div>
    );
  }

  // Invariantes de columna — no dependen de row
  const titleCol    = columns.find(c => c.title);
  const badgeCol    = columns.find(c => c.mobileBadge);
  const bodyColumns = columns.filter(c => !c.title && !c.mobileBadge && !c.mobileHidden && c.key !== 'acciones');

  // visibleActions por row — calculado una sola vez, consumido por tbody y cards
  const rowVisibleActions = new Map(
    data.map((row, index) => [
      row.id ?? index,
      actions.filter(a => !a.show || a.show(row))
    ])
  );

  return (
    <>
      {/* TABLA — visible en desktop */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`${column.sortable ? 'sortable-header' : ''} ${column.align ? `text-${column.align}` : ''}`.trim()}
                  onClick={() => column.sortable && handleSort(column.key)}
                  title={column.tooltip || column.label}
                >
                  {column.label}
                  {column.sortable && (
                    <i className={
                      sortField === column.key
                        ? `fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} sort-icon-active`
                        : 'fas fa-sort sort-icon-neutral'
                    }></i>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={animateRows ? 'animate-table-rows' : ''}>
            {data.map((row, index) => (
              <tr
                key={row.id || index}
                className={getRowClassName ? getRowClassName(row) : ''}
              >
                {columns.map((column) => {
                  if (column.key === 'acciones') {
                    const visibleActions = rowVisibleActions.get(row.id ?? index);
                    return (
                      <td key={column.key} className="text-center">
                        <div className="table-action-buttons">
                          {visibleActions.map(action => renderActionButton(action, row, 'btn-table-action'))}
                        </div>
                      </td>
                    );
                  }
                  return (
                    <td key={column.key} className={column.align ? `text-${column.align}` : ''}>
                      {renderCellValue(column, row, searchTerm)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CARDS — visibles en mobile (≤768px vía CSS) */}
      <div className={`mobile-card-list${!mobileCardRenderer ? ' animate-mobile-cards' : ''}`}>
        {mobileCardRenderer
          ? data.map((row, index) => mobileCardRenderer(row, index, rowVisibleActions.get(row.id ?? index)))
          : data.map((row, index) => {
              const visibleActions = rowVisibleActions.get(row.id ?? index);

              return (
                <div
                  key={row.id || index}
                  className={`mobile-card ${getRowClassName ? getRowClassName(row) : ''}`.trim()}
                >
                  <div className="mobile-card-header">
                    <span className="mobile-card-title">
                      {titleCol ? renderCellValue(titleCol, row, searchTerm) : ''}
                    </span>
                    {badgeCol && (
                      <span className="mobile-card-badge">
                        {renderCellValue(badgeCol, row, searchTerm)}
                      </span>
                    )}
                  </div>

                  <div className="mobile-card-body">
                    {bodyColumns.map(col => (
                      <div key={col.key} className="mobile-card-field">
                        <span className="mobile-card-label">
                          {typeof col.label === 'string' ? col.label : ''}
                        </span>
                        <div className="mobile-card-value">
                          {renderCellValue(col, row, searchTerm)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {visibleActions.length > 0 && (
                    <div className="card-actions">
                      {visibleActions.map(action => renderActionButton(action, row, action.className?.includes('danger') ? 'btn-table-action' : 'btn-sm'))}
                    </div>
                  )}
                </div>
              );
            })
        }
      </div>
    </>
  );
}

export default EntityTable;
